import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import AgentNode from "@/components/AgentNode";
import AgentDrawer from "@/components/AgentDrawer";
import FlowOverlay from "@/components/FlowOverlay";
import ArtifactsPanel from "@/components/ArtifactsPanel";
import HelperChat from "@/components/HelperChat";

const EXECUTION_ORDER = [
  "orchestrator", "research", "planning", "creative",
  "writing", "execution", "automation",
  "memory", "data", "communication", "feedback"
];

const LAYER_ORDER = [
  { layer: 0, name: "Orchestrator", subtitle: "Executive control" },
  { layer: 1, name: "Brain",        subtitle: "Strategy" },
  { layer: 2, name: "Hands",        subtitle: "Creation" },
  { layer: 3, name: "Legs",         subtitle: "Operations" },
  { layer: 4, name: "Organs",       subtitle: "Infrastructure" },
  { layer: 5, name: "Feedback",     subtitle: "Closed loop" },
];

const EXAMPLE_GOALS = [
  "Launch a 7-day rollout for a productivity Chrome extension targeting indie hackers.",
  "Plan a weekend pop-up dinner series for 24 guests with a 'forgotten grains' theme.",
  "Design an onboarding sequence for a B2B SaaS that helps founders write cold emails.",
];

function agentGridCols(n) {
  if (n === 1) return "grid-cols-1";
  if (n === 2) return "grid-cols-1 md:grid-cols-2";
  return "grid-cols-1 md:grid-cols-3";
}

export default function ConsolePage({ initialRunId, onOpenRun, onOpenInbox }) {
  const [agentsCatalog, setAgentsCatalog] = useState([]);
  const [run, setRun] = useState(null);
  const [goal, setGoal] = useState("");
  const [dispatching, setDispatching] = useState(false);
  const [openAgentId, setOpenAgentId] = useState(null);
  const [err, setErr] = useState("");
  const [integrations, setIntegrations] = useState(null);
  const pollRef = useRef(null);
  const topologyRef = useRef(null);

  // load static catalog + integration status
  useEffect(() => {
    api.get("/agents").then(({ data }) => setAgentsCatalog(data)).catch(() => {});
    api.get("/integrations/status").then(({ data }) => setIntegrations(data)).catch(() => {});
  }, []);

  // open run if requested from history (run id passed in via prop)
  useEffect(() => {
    if (initialRunId) loadAndPoll(initialRunId);
    // loadAndPoll is intentionally stable; effect should fire only on id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRunId]);

  function loadAndPoll(id) {
    stopPolling();
    api.get(`/runs/${id}`).then(({ data }) => {
      setRun(data);
      if (data.status === "running") startPolling(id);
    });
  }

  function startPolling(id) {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/runs/${id}`);
        setRun(data);
        if (data.status !== "running") stopPolling();
      } catch (e) {
        // Transient polling failure (network blip / cold start). Log but keep polling.
        console.warn("[ConsolePage] poll failed:", e?.message || e);
      }
    }, 900);
  }
  function stopPolling() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }
  useEffect(() => () => stopPolling(), []);

  async function dispatch() {
    if (!goal.trim() || goal.trim().length < 4) {
      setErr("Goal must be at least 4 characters."); return;
    }
    setErr(""); setDispatching(true);
    try {
      const { data } = await api.post("/runs", { goal });
      onOpenRun?.(data.id);
      loadAndPoll(data.id);
      setGoal("");
    } catch (e) {
      setErr(e?.response?.data?.detail?.toString?.() || "Dispatch failed.");
    } finally { setDispatching(false); }
  }

  // Build the display: prefer live run agents; fall back to catalog when idle.
  const displayAgents = run
    ? run.agents
    : agentsCatalog.map(a => ({ ...a, status: "idle", output: "" }));

  const grouped = LAYER_ORDER.map(L => ({
    ...L,
    agents: displayAgents.filter(a => a.layer === L.layer),
  })).filter(L => L.agents.length > 0);

  const completedCount = displayAgents.filter(a => a.status === "completed").length;
  const totalCount = displayAgents.length || 11;
  const progress = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  const openAgent = openAgentId
    ? displayAgents.find(a => a.id === openAgentId)
    : null;

  // Dispatch button label — flattened from a nested ternary for readability.
  let dispatchLabel = "Dispatch goal →";
  if (dispatching) dispatchLabel = "Dispatching…";
  else if (run && run.status === "running") dispatchLabel = "Run in progress";

  const dispatchDisabled = dispatching || (run && run.status === "running");

  return (
    <div className="grain blueprint-grid">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 lg:py-12">
        {/* Hero / goal entry */}
        <section className="border hairline bg-[#071a35]/70 backdrop-blur">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-7 p-8 lg:p-10 border-r-0 lg:border-r hairline">
              <div className="label-xs mb-3">Dispatch · new directive</div>
              <h1 className="font-serif-display text-4xl lg:text-6xl leading-[0.98] tracking-tighter mb-2">
                What should the<br/>
                <span className="italic text-[#00B4E6]">organism</span> work on?
              </h1>
              <p className="text-[15px] text-[#A8B3C7] max-w-md mt-3">
                One sentence, plainspoken. The Orchestrator will decompose it and assign the rest.
              </p>

              <div className="mt-6">
                <textarea
                  className="input-line"
                  placeholder="e.g. Plan a 7-day product launch for a B2B SaaS…"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  rows={3}
                  data-testid="goal-input"
                />
              </div>

              {err && (
                <div className="mt-3 border-l-2 border-[#FF5B5B] pl-3 py-1 font-mono text-xs text-[#FF5B5B]" data-testid="dispatch-error">
                  {err}
                </div>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={dispatch}
                  disabled={dispatchDisabled}
                  className="btn-primary"
                  data-testid="dispatch-btn"
                >
                  {dispatchLabel}
                </button>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLE_GOALS.map((g, i) => (
                    <button
                      key={g}
                      onClick={() => setGoal(g)}
                      className="font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 border hairline hover:bg-black hover:text-[#010f23] transition-colors"
                      data-testid={`example-goal-${i}`}
                    >
                      Example {i+1}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Run status panel */}
            <div className="lg:col-span-5 p-8 lg:p-10 bg-[#071a35]/40">
              <div className="label-xs mb-3">Current run</div>
              {run ? (
                <>
                  <div className="font-serif-display text-2xl italic leading-tight mb-4" data-testid="run-goal-display">
                    “{run.goal}”
                  </div>
                  <div className="grid grid-cols-3 border hairline">
                    <div className="p-3 border-r hairline">
                      <div className="label-xs">Status</div>
                      <div className="font-mono text-sm mt-1 capitalize" data-testid="run-status">{run.status}</div>
                    </div>
                    <div className="p-3 border-r hairline">
                      <div className="label-xs">Progress</div>
                      <div className="font-mono text-sm mt-1" data-testid="run-progress">{completedCount}/{totalCount}</div>
                    </div>
                    <div className="p-3">
                      <div className="label-xs">Run id</div>
                      <div className="font-mono text-[11px] mt-1 truncate" title={run.id}>{run.id.slice(0,8)}…</div>
                    </div>
                  </div>
                  <div className="mt-4 h-[3px] bg-[#FFFFFF22] relative">
                    <motion.div
                      className="absolute left-0 top-0 h-full bg-[#00B4E6]"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                </>
              ) : (
                <div className="text-[#A8B3C7] font-mono text-sm">
                  No run yet. Dispatch a goal to wake the eleven.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Layered agent topology */}
        <section className="mt-10 lg:mt-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-xs">II · The topology</div>
              <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight">
                Three layers. <span className="italic">Eleven agents.</span> One organism.
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A8B3C7]">
              <span className="flex items-center gap-2"><span className="dot idle" /> Idle</span>
              <span className="flex items-center gap-2"><span className="dot working" /> Active</span>
              <span className="flex items-center gap-2"><span className="dot completed" /> Complete</span>
            </div>
          </div>

          <div className="border-t border-l hairline relative" ref={topologyRef}>
            {grouped.map((L) => (
              <div key={L.layer} className="grid grid-cols-1 md:grid-cols-12">
                <div className="md:col-span-3 border-r border-b hairline p-6 lg:p-8 bg-[#071a35]/30 flex flex-col justify-between min-h-[150px]">
                  <div>
                    <div className="label-xs">Layer {String(L.layer).padStart(2,"0")}</div>
                    <div className="font-serif-display text-3xl italic mt-1">{L.name}</div>
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#A8B3C7]">
                    {L.subtitle} · {L.agents.length} agent{L.agents.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className={"md:col-span-9 grid " + agentGridCols(L.agents.length)}>
                  {L.agents.map((a, i) => (
                    <AgentNode
                      key={a.id}
                      agent={a}
                      idx={i}
                      onOpen={setOpenAgentId}
                      isActive={a.status === "working" || a.status === "thinking"}
                    />
                  ))}
                </div>
              </div>
            ))}
            <FlowOverlay
              containerRef={topologyRef}
              agentOrder={EXECUTION_ORDER}
              statusById={Object.fromEntries(displayAgents.map((a) => [a.id, a.status]))}
            />
          </div>
        </section>

        {/* III · Artifacts (cross-run memory, web search, calendar, delivery) */}
        <ArtifactsPanel
          run={run}
          integrationsStatus={integrations}
          onOpenInbox={onOpenInbox}
        />

        {/* Footer note */}
        <section className="mt-14 grid grid-cols-1 lg:grid-cols-3 border-t border-l hairline">
          {[
            ["Streaming output","Each agent streams live tokens into its panel. Tap a node to read the full thinking."],
            ["Closed loop","After all agents act, the Feedback Loop critiques the run and proposes upgrades."],
            ["Persistent memory","Memory captures voice and decisions. Future runs inherit the context."],
          ].map(([t,b]) => (
            <div key={t} className="border-r border-b hairline p-6">
              <div className="label-xs">Principle</div>
              <div className="font-serif-display text-2xl mt-1">{t}</div>
              <p className="mt-2 text-[14px] leading-relaxed text-[#A8B3C7]">{b}</p>
            </div>
          ))}
        </section>
      </div>

      <AgentDrawer agent={openAgent} onClose={() => setOpenAgentId(null)} />
      <HelperChat />
    </div>
  );
}
